import { useSaveUserEdits } from "@/data-preparation-tool/inspect-and-publish/refine-and-publish-utils";
import { waitForHierarchySaveToFinish } from "@/data-preparation-tool/store/data-preparation-ui/wait-for-hierarchy-save";
import {
  selectRegistrationRevision,
  selectRevisionEntities,
} from "@/data-preparation-tool/store/revision/revision-entity-selectors";
import { selectIsRevisionUserModified } from "@/data-preparation-tool/store/user-edits/data-preparation-user-edit-selectors";
import { SavingUserEditsDialog } from "@/data-preparation-tool/ui/data-preparation-dialogs/saving-user-edits-dialog";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { useHasDraftInProgress } from "@/hooks/use-has-draft-in-progress";
import { useIsOnDraftPage } from "@/hooks/use-is-on-draft-page";
import { useAppSelector, useAppStore } from "@/store/store-hooks";
import { selectCurrentUser } from "@/store/user-selectors";
import {
  FaroBreadcrumb,
  FaroBreadcrumbs,
  useBreakpointMdUp,
} from "@faro-lotv/flat-ui";
import {
  getActiveProjectsPage,
  getProjectDetailsPage,
  selectDashboardUrl,
  selectProjectId,
  selectProjectName,
} from "@faro-lotv/project-source";
import { CaptureTreeEntityType } from "@faro-lotv/service-wires";
import useMediaQuery from "@mui/system/useMediaQuery";
import { useCallback, useMemo, useState, type JSX } from "react";
import { LiveDraftToggleButton } from "./live-draft-toggle-button";

/** @returns the breadcrumbs navigation for the project main ui */
export function ProjectBreadcrumbs(): JSX.Element | null {
  const projectName = useAppSelector(selectProjectName);
  const projectId = useAppSelector(selectProjectId);
  const dashboardUrl = useAppSelector(selectDashboardUrl);

  // In Live view, the toggle button group for switching to Draft view is shown if the following
  // conditions are met (otherwise a "Live" caption is shown):
  //  - there are draft changes or the project contains relevant data.
  //  - the user is logged in, since Data Management requires a logged-in user.
  // In Draft view, the toggle button group is always shown since switching to Live view should
  // always be possible.
  const hasDraft = useHasDraftInProgress(projectId);
  const isOnDraftPage = useIsOnDraftPage();
  const isRevisionUserModified = useAppSelector(selectIsRevisionUserModified);
  const { handleErrorWithToast } = useErrorHandlers();
  const { isSavingUserEdits, saveUserEdits } = useSaveUserEdits();
  const revision = useAppSelector(selectRegistrationRevision);
  const isSmallScreen = !useBreakpointMdUp();
  const isTouch = useMediaQuery("(pointer: coarse)");
  const isEditingSupported = !isSmallScreen && !isTouch;
  const currentUser = useAppSelector(selectCurrentUser);
  const store = useAppStore();
  const [isWaitingForHierarchySave, setIsWaitingForHierarchySave] =
    useState(false);

  const revisionEntities = useAppSelector(selectRevisionEntities);
  // There's project content to show in the Draft view if there's at least one entity of any kind,
  // including deleted entities, that's not the Root.
  const hasContentForDraftView =
    revisionEntities.length > 1 ||
    (revisionEntities.length === 1 &&
      revisionEntities[0].type !== CaptureTreeEntityType.root);

  const isToggleBtnVisible =
    !!projectId &&
    (isOnDraftPage || ((hasDraft || hasContentForDraftView) && !!currentUser));

  const projectsUrl = dashboardUrl
    ? getActiveProjectsPage(dashboardUrl)
    : undefined;
  const projectUrl =
    dashboardUrl && projectId
      ? getProjectDetailsPage(dashboardUrl, projectId)
      : undefined;

  const handleBeforeToggle = useCallback(async () => {
    // Only called when exiting the draft view, so it's safe to show the dialog here.
    setIsWaitingForHierarchySave(true);
    try {
      await waitForHierarchySaveToFinish(store);
    } finally {
      setIsWaitingForHierarchySave(false);
    }

    if (isRevisionUserModified && revision) {
      try {
        await saveUserEdits(revision);
      } catch (error) {
        handleErrorWithToast({
          error,
          title: "Failed to save edits before switching views",
        });
        throw error;
      }
    }
  }, [
    handleErrorWithToast,
    isRevisionUserModified,
    revision,
    saveUserEdits,
    store,
  ]);

  const breadcrumbs: FaroBreadcrumb[] = useMemo(() => {
    if (!projectName) {
      return [];
    }

    return [
      {
        text: "Projects",
        link: projectsUrl,
      },
      {
        text: projectName,
        link: projectUrl,
        shouldNotTranslate: true,
      },
      {
        text: isToggleBtnVisible ? (
          <LiveDraftToggleButton
            onBeforeNavigate={isOnDraftPage ? handleBeforeToggle : undefined}
          />
        ) : (
          "Live"
        ),
        showTooltip: false,
      },
    ];
  }, [
    projectName,
    projectsUrl,
    projectUrl,
    isToggleBtnVisible,
    isOnDraftPage,
    handleBeforeToggle,
  ]);

  const handleBreadcrumbClick = useCallback(
    async (breadcrumb: FaroBreadcrumb) => {
      // Only wired up while on the draft view, so it's safe to show the dialog here.
      setIsWaitingForHierarchySave(true);
      try {
        await waitForHierarchySaveToFinish(store);
      } finally {
        setIsWaitingForHierarchySave(false);
      }

      const shouldSaveBeforeNavigation = isRevisionUserModified && revision;

      if (shouldSaveBeforeNavigation) {
        try {
          await saveUserEdits(revision);
        } catch (error) {
          handleErrorWithToast({
            error,
            title: "Failed to save edits before leaving the project",
          });
          return;
        }
      }

      if (breadcrumb.link) {
        window.location.assign(breadcrumb.link);
      }
    },
    [
      isRevisionUserModified,
      revision,
      saveUserEdits,
      handleErrorWithToast,
      store,
    ],
  );

  if (!projectName) return null;

  return (
    <>
      <FaroBreadcrumbs
        breadcrumbs={breadcrumbs}
        onBreadcrumbClick={
          isOnDraftPage && isEditingSupported
            ? handleBreadcrumbClick
            : undefined
        }
      />
      <SavingUserEditsDialog
        open={isSavingUserEdits || isWaitingForHierarchySave}
      />
    </>
  );
}
